import { Box, Button } from "@mui/material";
import { ErrorMessage, Form, Formik } from "formik";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";
import ApiHelperFunction from "../../api";
import { UploadIcon } from "../../assets/Icon/dashboredIcon";
import { useAppDispatch } from "../../stores/store";
import { setToast } from "../../stores/toast/toastslice";
import CustomModal from "../common-components/CustomModal";
import ListLoadingNew from "../common-components/ListLoadingNew";
import UploadFileField from "../common-components/UploadFileField";
import {
  StyledButtonContainer,
  StyledContainer,
  StyledHeader,
  StyledLink,
} from "../style/ititn/itinUpload";

interface StepProp {
  handlePrev: () => void;
  onCancelEdit?: () => void; 
  edit?: boolean;
  hasSigned?: any;
  edit2848?: boolean;
  uploadedFileW7?: File | string | null; 
  uploadedFile2848?: File | string | null; 
  onSuccess?:()=>void;
}
const data = [
  {
    // title: "When signing W7 form",
    heading: "When signing W7 form, please follow:",
    instructions: [
      "Please sign with BLUE PEN.",
      "Do not change anything on the form, including the date; only provide your signature.",
      "Your signature must match the signature on your passport.",
    ],
  },
  {
    // title: "FORM 2848 Signature Instructions",
    heading: "When signing 2848 form, please follow:",
    instructions: [
      "Sign with BLUE PEN, fill in the date in US format (MM-DD-YYYY), and write the full signer's name.",
      "Your signature must match the signature on your passport.",
      "On the 2848 form, you need to write your full name in the 'Print Name' section by hand. You don't need to fill in the title.",
    ],
  },
];
const UploadSignComponentItin = ({
  handlePrev,
  hasSigned,
  edit,
  edit2848,
  uploadedFileW7,
  uploadedFile2848,
  onCancelEdit,
  onSuccess,
}: StepProp) => {
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("user_token");
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const handleModalOpen = () => setIsSignModalOpen(true);
  const handleModalClose = () => setIsSignModalOpen(false);

  // ✅ Validation Schema
  const validationSchema = Yup.object({
    w7_signed_form: edit2848
      ? Yup.mixed().nullable()
      : Yup.mixed().required("W-7 Form is required"),
    form_2848_signed_form: edit
      ? Yup.mixed().nullable()
      : Yup.mixed().required("2848 Form is required"),
  });

  // ✅ Handle Form Submission
  const handleSubmit = async (values: {
    w7_signed_form: File | string | null;
    form_2848_signed_form?: File | string | null;
  }) => {
    setLoading(true);
    const formData = new FormData();
    const isW7File =
      values.w7_signed_form && typeof values.w7_signed_form !== "string";
    const is2848File =
      values.form_2848_signed_form &&
      typeof values.form_2848_signed_form !== "string";

    if (!isW7File && !is2848File) {
      if ((edit || edit2848)  && onCancelEdit) {
        onCancelEdit(); // 👈 Reset edit mode in parent
      }
      setLoading(false);
      return;
    }
    // Append files only if they are valid
    if (values.w7_signed_form && typeof values.w7_signed_form !== "string") {
      formData.append("w7_signed_form", values.w7_signed_form);
    }

    if (
      values.form_2848_signed_form &&
      typeof values.form_2848_signed_form !== "string"
    ) {
      formData.append("form_2848_signed_form", values.form_2848_signed_form);
    }
    if (id) {
      formData.append("order_id", id);
    }
    try {
      const response = await ApiHelperFunction({
        url: "itin/create-report/",
        method: "POST",
        data: formData,
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });

      if (response?.data?.status === 1) {
        dispatch<any>(
          setToast({
            message: response?.data?.message,
            type: "success",
            title: "Success",
          })
        );
        setTimeout(() => {
          window.location.reload();
        }, 30);
      } else {
        dispatch<any>(
          setToast({
            message: response?.data?.message,
            type: "error",
            title: "Error",
          })
        );
      }
      // if (onSuccess) {
 
      //   onSuccess();
      // }
    } catch (error) {
      dispatch<any>(
        setToast({
          message: "An error occurred during submission.",
          type: "error",
          title: "Error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        w7_signed_form: uploadedFileW7 || null,
        form_2848_signed_form: uploadedFile2848 || null,
      }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values, submitForm }) => (
        <Form>
          <StyledContainer>
            <StyledHeader>
              Upload Signed Form
              <StyledLink to="" onClick={handleModalOpen}>
                Read Signature Instruction
              </StyledLink>
            </StyledHeader>
            <Box sx={{ height: "1px", background: "#EAF1FA" }}></Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {(hasSigned || edit) && (
                <Box sx={{ width: "100%", maxWidth: "400px" }}>
                  <UploadFileField
                    title="W-7 Form"
                    desc={
                      values.w7_signed_form
                        ? "File Selected"
                        : "Click here or drag & drop"
                    }
                    icon={<UploadIcon />}
                    accept="application/pdf, image/jpeg, image/png"
                    onChange={(file) => setFieldValue("w7_signed_form", file)}
                    fileUrl={
                      typeof values.w7_signed_form === "string"
                        ? values.w7_signed_form
                        : undefined
                    }
                  />

                  <Box
                    sx={{ color: "#D32F2F", fontSize: "14px", mt: 1, marginLeft: 2 ,mb:2}}
                  >
                    <ErrorMessage name="w7_signed_form" component="div" className="ms-4" />
                  </Box>
                </Box>
              )}

              {(hasSigned || edit2848) && (
                <Box sx={{ width: "100%", maxWidth: "400px" }}>
                  <UploadFileField
                    title="2848 Form"
                    desc={
                      values.form_2848_signed_form
                        ? "File Selected"
                        : "Click here or drag & drop"
                    }
                    icon={<UploadIcon />}
                    accept="application/pdf, image/jpeg, image/png"
                    onChange={(file) =>
                      setFieldValue("form_2848_signed_form", file)
                    }
                    fileUrl={
                      typeof values.form_2848_signed_form === "string"
                        ? values.form_2848_signed_form
                        : undefined
                    }
                  />
                  <Box
                    sx={{ color: "#D32F2F", fontSize: "14px", mt: 1, marginLeft: 2 ,mb:2 }}
                  >
                    <ErrorMessage
                      name="form_2848_signed_form"
                      component="div"
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <StyledButtonContainer>
              <Button
                variant="contained"
                sx={{
                  minWidth: "191px",
                  height: "52px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  background: loading
                    ? "#D3D3D3"
                    : "linear-gradient(98.24deg, #2977CC 4.13%, #1D538F 128.24%)",
                  color: "#FFFFFF",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                type="button"
                onClick={submitForm}
                disabled={loading}
              >
                {loading ? <ListLoadingNew /> : "Submit"}
              </Button>
            </StyledButtonContainer>

            <CustomModal
              open={isSignModalOpen}
              handleClose={handleModalClose}
              title="Application Signature Instruction"
              data={data}
              // desc="After signing please scan and sent to us."
            />
          </StyledContainer>
        </Form>
      )}
    </Formik>
  );
};

export default UploadSignComponentItin;
